import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, Info, ArrowUpRight, AlertTriangle } from 'lucide-react';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }
function fmtCurrency(n) { if (n == null) return '—'; return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

function ScoreGauge({ score, label }) {
  const v = Math.max(0, Math.min(100, score || 0));
  const color = v >= 75 ? '#4A7C5E' : v >= 50 ? '#D4A85C' : v >= 25 ? '#D58668' : '#C75D5D';
  return (
    <div className="flex items-center gap-4">
      <svg width="92" height="92" viewBox="0 0 92 92">
        <circle cx="46" cy="46" r="36" stroke="#F0E7D2" strokeWidth="10" fill="none" />
        <circle cx="46" cy="46" r="36" stroke={color} strokeWidth="10" fill="none"
                strokeLinecap="round" strokeDasharray={2 * Math.PI * 36}
                strokeDashoffset={2 * Math.PI * 36 - (v / 100) * (2 * Math.PI * 36)}
                transform="rotate(-90 46 46)" />
        <text x="46" y="50" textAnchor="middle" style={{ fontSize: '18px', fill: '#1A2622', fontWeight: 600 }}>
          {Math.round(v)}
        </text>
      </svg>
      <div className="text-[12.5px] font-semibold text-[#1A2622]">{label}</div>
    </div>
  );
}

function SpecialtyTag({ type }) {
  const styles = {
    PMR: { bg: '#E5F0EA', fg: '#2D5F4F' },
    IM: { bg: '#FBE6CC', fg: '#A06628' },
    APP: { bg: '#E0EAF8', fg: '#34538F' },
  };
  const s = styles[type] || { bg: '#F0E7D2', fg: '#5A6660' };
  const label = { PMR: 'PM&R', IM: 'IM', APP: 'NP/PA' }[type] || type;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: s.bg, color: s.fg }}>{label}</span>;
}

export default function PhysicianAlignmentTab({ ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.facilityPhysicians(ccn, { limit: 50 })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || 'No physician data available'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [ccn]);

  if (loading) {
    return <div className="mt-7 rounded-xl bg-white border border-[#E5DCC4] p-8 text-[13px] text-[#7A857F]" data-testid="phys-tab-loading">Loading physician alignment intelligence…</div>;
  }
  if (error || !data) {
    return (
      <div className="mt-7 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-10 text-center" data-testid="phys-tab-empty">
        <Stethoscope size={28} className="text-[#A06628] mx-auto" />
        <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#FBE6CC] text-[#A06628]">
          Coming Soon
        </div>
        <div className="mt-3 font-display text-xl text-[#1A2622]">Physician alignment insights</div>
        <p className="mt-2 text-[13px] text-[#5A6660] max-w-[520px] mx-auto leading-relaxed">
          Loading the latest Medicare Part B SNF E&amp;M claim graph and CMS Doctors &amp; Clinicians directory. Aligned physicians, specialty coverage, and confidence-tiered relationships will surface here shortly.
        </p>
        <div className="mt-3 text-[11px] text-[#7A857F]">Source: Medicare Physician PUF + CMS Doctors &amp; Clinicians DAC file</div>
      </div>
    );
  }

  const s = data.summary || {};
  const items = data.items || [];
  const isEmpty = data.count === 0 || s.aligned_physicians === 0;

  if (isEmpty) {
    return (
      <section className="mt-7" data-testid="phys-tab-empty">
        <div className="rounded-xl bg-white border border-[#E5DCC4] p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-12 h-12 rounded-full bg-[#F5EFDD] flex items-center justify-center">
              <Stethoscope size={22} className="text-[#A8B0AA]" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-[#1A2622]">No publicly identified SNF-aligned physicians in this catchment yet</h3>
              <p className="mt-2 text-[13px] text-[#3A4A44] leading-relaxed">
                {s.methodology || 'Alignment requires a physician with public Medicare Part B SNF E&M claims (HCPCS 99304-99316) located in the same ZIP or city as this facility. Rural or low-volume markets often have no match — this is an additive layer, never a quality signal.'}
              </p>
              <div className="mt-3 flex items-start gap-1.5 text-[11.5px] text-[#A8B0AA] leading-relaxed">
                <Info size={11} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Source:</strong> {data.source}. <strong>Secondary:</strong> {data.secondary_source}.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-7" data-testid="phys-tab">
      {/* Score + sub-scores */}
      <div className="rounded-xl bg-white border border-[#E5DCC4] p-5 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          <ScoreGauge score={s.alignment_score || 0} label="Physician alignment score" />
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#E5F0EA] text-[#2D5F4F]">
              <Stethoscope size={12}/> {s.aligned_physicians || 0} likely affiliated physicians
            </div>
            <p className="mt-3 text-[13px] text-[#3A4A44] leading-relaxed">
              {(s.aligned_physicians || 0)} physicians have public Medicare Part B SNF E&M claims and are located in the same ZIP / city as this facility.
              {s.gaps?.length > 0 && (
                <span className="block mt-1.5 text-[#C75D5D]"><AlertTriangle size={11} className="inline mr-1"/> Coverage gaps: {s.gaps.join(', ')}</span>
              )}
            </p>
            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
              {[['Coverage', s.sub_scores?.coverage, 30],
                ['Volume', s.sub_scores?.volume, 30],
                ['Density', s.sub_scores?.density, 20],
                ['Diversity', s.sub_scores?.diversity, 10],
                ['vs state', s.sub_scores?.relative_activity, 10]].map(([k, v, max]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A857F]">{k}</div>
                  <div className="text-[12.5px] font-semibold text-[#1A2622] tabular-nums">{v ?? '—'}<span className="text-[10px] text-[#A8B0AA] ml-0.5">/{max}</span></div>
                  <div className="h-1 mt-1 bg-[#F0E7D2] rounded-full overflow-hidden">
                    <div className="h-full bg-[#4A7C5E]" style={{ width: `${Math.min(100, ((v || 0) / max) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Specialty counts strip */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Aligned physicians', value: s.aligned_physicians, accent: 'text-[#1A2622]' },
          { label: 'PM&R', value: s.pmr_count, accent: 'text-[#2D5F4F]' },
          { label: 'Internal Medicine', value: s.im_count, accent: 'text-[#A06628]' },
          { label: 'NP / PA', value: s.app_count, accent: 'text-[#34538F]' },
          { label: 'Total SNF services', value: fmtInt(s.total_snf_services) },
        ].map((k) => (
          <div key={k.label} className="p-3 rounded-xl bg-white border border-[#E5DCC4]">
            <div className={`font-display text-xl ${k.accent || ''}`}>{k.value ?? '—'}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Physicians table */}
      <div className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
          <Stethoscope size={14} className="text-[#4A7C5E]"/>
          <h3 className="font-display text-base font-semibold text-[#1A2622]">Likely affiliated physicians</h3>
          <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Top {Math.min(items.length, 50)} by SNF claims volume</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
              <tr>
                <th className="text-left px-4 py-2.5">Physician</th>
                <th className="text-left px-4 py-2.5">Specialty</th>
                <th className="text-left px-4 py-2.5">Confidence</th>
                <th className="text-right px-4 py-2.5">SNF services</th>
                <th className="text-right px-4 py-2.5">Beneficiaries</th>
                <th className="text-right px-4 py-2.5">Score</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.npi} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`phys-row-${p.npi}`}>
                  <td className="px-4 py-2.5">
                    <Link to={`/physicians/${p.npi}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">
                      {p.physician_name}
                    </Link>
                    <div className="text-[10.5px] text-[#7A857F]">NPI {p.npi} · {p.physician_city || ''}{p.physician_city && p.physician_state ? ', ' : ''}{p.physician_state || ''}</div>
                  </td>
                  <td className="px-4 py-2.5 text-[#3A4A44]">
                    <div className="truncate max-w-[220px]" title={p.specialty}>{p.specialty || '—'}</div>
                    <div className="mt-1 flex gap-1">
                      {p.is_pm_r && <SpecialtyTag type="PMR"/>}
                      {p.is_internal_med && <SpecialtyTag type="IM"/>}
                      {p.is_app && <SpecialtyTag type="APP"/>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${p.confidence === 'high' ? 'bg-[#E5F0EA] text-[#2D5F4F]' : 'bg-[#FBE6CC] text-[#A06628]'}`}>
                      {p.confidence}
                    </span>
                    <div className="text-[10.5px] text-[#7A857F] mt-0.5">{p.reason?.replace(/_/g, ' ')}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums">{fmtInt(p.snf_services)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(p.snf_beneficiaries)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-block font-semibold text-[#4A7C5E]">{p.alignment_score?.toFixed(0)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <Link to="/physician-intelligence" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="phys-link-dashboard">
          Physician Intelligence dashboard <ArrowUpRight size={13}/>
        </Link>
      </div>

      <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          <strong>Methodology:</strong> {s.methodology || 'Alignment is inferred from public Part B SNF E&M claims (HCPCS 99304-99316) and same-ZIP/same-city proximity. These are public-data ESTIMATES — never patient referrals.'}
          {' '}<strong>Source:</strong> {data.source}. {' '}
          <strong>Secondary:</strong> {data.secondary_source}.
          {s.updated_at && <> Updated {new Date(s.updated_at).toLocaleDateString()}.</>}
        </span>
      </div>
    </section>
  );
}
